import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import BaseLayout from './base_layout.vue';
const SCROLL_CONTAINER_SELECTOR = '.panel-content-inner';
export default {
    name: 'DetailLayout',
    components: {
        BaseLayout
    },
    mixins: [
        glSlotsMixin
    ],
    props: {
        ...BaseLayout.props,
        showSidebar: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    watch: {
        loading () {
            // The sidebar only renders once loading is done, so (re-)wire against it then.
            this.$nextTick(this.setupSidebarSync);
        }
    },
    mounted () {
        this.$nextTick(this.setupSidebarSync);
        window.addEventListener('resize', this.scheduleSyncSidebarSpace);
    },
    beforeDestroy () {
        cancelAnimationFrame(this.syncFrame);
        this.scrollContainer?.removeEventListener('scroll', this.scheduleSyncSidebarSpace);
        window.removeEventListener('resize', this.scheduleSyncSidebarSpace);
        this.contentResizeObserver?.disconnect();
    },
    methods: {
        setupSidebarSync () {
            const scrollContainer = this.$refs.sidebar?.closest(SCROLL_CONTAINER_SELECTOR);
            if (!scrollContainer || scrollContainer === this.scrollContainer) return;
            // Detach from a previously resolved container so it can't leak a listener.
            this.scrollContainer?.removeEventListener('scroll', this.scheduleSyncSidebarSpace);
            this.scrollContainer = scrollContainer;
            this.scrollContainer.addEventListener('scroll', this.scheduleSyncSidebarSpace, {
                passive: true
            });
            // Re-measure when content above the sidebar changes height after mount.
            this.contentResizeObserver?.disconnect();
            if (typeof ResizeObserver !== 'undefined') {
                this.contentResizeObserver = new ResizeObserver(this.scheduleSyncSidebarSpace);
                this.contentResizeObserver.observe(this.$el);
            }
            this.syncSidebarSpace();
        },
        onStickyChange () {
            // The sticky header height moves the stuck sidebar without a scroll event.
            this.scheduleSyncSidebarSpace();
        },
        scheduleSyncSidebarSpace () {
            // Coalesce bursts of scroll/resize events into one measurement per frame.
            cancelAnimationFrame(this.syncFrame);
            this.syncFrame = requestAnimationFrame(this.syncSidebarSpace);
        },
        syncSidebarSpace () {
            const { sidebar } = this.$refs;
            if (!sidebar || !this.scrollContainer) return;
            const space = this.measureSidebarSpace(sidebar);
            sidebar.style.setProperty('--detail-layout-sidebar-space', `${Math.max(space, 0)}px`);
        },
        measureSidebarSpace (sidebar) {
            const container = this.scrollContainer;
            const containerRect = container.getBoundingClientRect();
            const style = window.getComputedStyle(sidebar);
            if (style.position !== 'sticky') {
                return sidebar.getBoundingClientRect().top - containerRect.top;
            }
            // Derived from the layout, not the sidebar's own rect, which can be stale mid-scroll.
            // The sticky inset applies to the border box, so the margin only affects naturalTop.
            const scrollportTop = containerRect.top + container.clientTop;
            const marginTop = parseFloat(style.marginTop) || 0;
            const inset = parseFloat(style.top);
            const naturalTop = sidebar.parentElement.getBoundingClientRect().top + marginTop;
            const stuckTop = Number.isNaN(inset) ? -Infinity : scrollportTop + inset;
            return Math.max(naturalTop, stuckTop) - scrollportTop;
        }
    }
};
